import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

export interface MultiSelectFilterOption {
    value: string;
    label: string;
    count?: number;
}

interface MultiSelectFilterProps {
    label: string;
    allLabel: string;
    options: MultiSelectFilterOption[];
    values: string[];
    onApply: (values: string[]) => void;
    disabled?: boolean;
    searchable?: boolean;
    align?: 'left' | 'right';
}

const sameValues = (left: string[], right: string[]) => (
    left.length === right.length && left.every(value => right.includes(value))
);

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
    label,
    allLabel,
    options,
    values,
    onApply,
    disabled = false,
    searchable = false,
    align = 'left',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [draftValues, setDraftValues] = useState<string[]>(values);
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const closeOnOutsideClick = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                triggerRef.current?.focus();
            }
        };

        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [isOpen]);

    const filteredOptions = useMemo(() => {
        const normalisedQuery = query.trim().toLowerCase();
        if (!normalisedQuery) return options;
        return options.filter(option => option.label.toLowerCase().includes(normalisedQuery));
    }, [options, query]);

    const selectedLabel = useMemo(() => {
        if (values.length === 0) return allLabel;
        if (values.length === 1) {
            return options.find(option => option.value === values[0])?.label ?? allLabel;
        }
        return `${values.length} selected`;
    }, [allLabel, options, values]);

    const openMenu = () => {
        setDraftValues(values);
        setQuery('');
        setIsOpen(true);
    };

    const toggleValue = (value: string) => {
        setDraftValues(current => current.includes(value)
            ? current.filter(item => item !== value)
            : [...current, value]);
    };

    const applySelection = () => {
        const orderedValues = options
            .map(option => option.value)
            .filter(value => draftValues.includes(value));
        onApply(orderedValues);
        setIsOpen(false);
        triggerRef.current?.focus();
    };

    return (
        <div ref={containerRef} className="relative w-full">
            <button
                ref={triggerRef}
                type="button"
                onClick={() => isOpen ? setIsOpen(false) : openMenu()}
                disabled={disabled}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                className="flex min-h-11 w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-800 transition-colors hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-2 disabled:cursor-wait disabled:bg-gray-50 disabled:text-gray-400"
            >
                <span className="min-w-0 truncate">{selectedLabel}</span>
                <span className="flex flex-none items-center gap-1.5">
                    {values.length > 1 && (
                        <span className="rounded bg-[#69BE28]/10 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-[#4a8a1c]">
                            {values.length}
                        </span>
                    )}
                    <ChevronDown
                        className={`h-4 w-4 text-gray-500 transition-transform motion-reduce:transition-none ${isOpen ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                    />
                </span>
            </button>

            {isOpen && (
                <div
                    role="dialog"
                    aria-label={`${label} filter`}
                    className={`absolute z-50 mt-2 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_12px_35px_rgba(15,25,10,0.16)] ${align === 'right' ? 'right-0' : 'left-0'}`}
                >
                    <div className="flex items-center justify-between border-b border-gray-100 px-3.5 py-3">
                        <div>
                            <p className="text-sm font-semibold text-gray-900">{label}</p>
                            <p className="mt-0.5 text-xs text-gray-500">Choose one or more</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c]"
                            aria-label={`Close ${label.toLowerCase()} filter`}
                        >
                            <X className="h-4 w-4" aria-hidden="true" />
                        </button>
                    </div>

                    {searchable && options.length > 7 && (
                        <div className="border-b border-gray-100 p-2.5">
                            <label className="relative block">
                                <span className="sr-only">Search {label.toLowerCase()}</span>
                                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" aria-hidden="true" />
                                <input
                                    type="search"
                                    value={query}
                                    onChange={event => setQuery(event.target.value)}
                                    placeholder={`Search ${label.toLowerCase()}…`}
                                    className="min-h-9 w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4a8a1c]"
                                    autoFocus
                                />
                            </label>
                        </div>
                    )}

                    <div className="max-h-64 overflow-y-auto p-1.5">
                        {filteredOptions.length > 0 ? filteredOptions.map(option => {
                            const checked = draftValues.includes(option.value);
                            return (
                                <label
                                    key={option.value}
                                    className={`flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition-colors ${checked ? 'bg-[#69BE28]/8 text-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleValue(option.value)}
                                        className="sr-only"
                                    />
                                    <span className={`flex h-4 w-4 flex-none items-center justify-center rounded border ${checked ? 'border-[#4a8a1c] bg-[#4a8a1c] text-white' : 'border-gray-300 bg-white'}`} aria-hidden="true">
                                        {checked && <Check className="h-3 w-3" />}
                                    </span>
                                    <span className="min-w-0 flex-1 truncate">{option.label}</span>
                                    {option.count !== undefined && (
                                        <span className="text-xs tabular-nums text-gray-500">{option.count}</span>
                                    )}
                                </label>
                            );
                        }) : (
                            <p className="px-3 py-6 text-center text-sm text-gray-500">No matches</p>
                        )}
                    </div>

                    <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/70 px-3 py-2.5">
                        <button
                            type="button"
                            onClick={() => setDraftValues([])}
                            disabled={draftValues.length === 0}
                            className="min-h-9 rounded-md px-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] disabled:cursor-default disabled:opacity-40"
                        >
                            Clear
                        </button>
                        <button
                            type="button"
                            onClick={applySelection}
                            disabled={sameValues(draftValues, values)}
                            className="min-h-9 rounded-md bg-[#3d701a] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#315a15] focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-2 disabled:cursor-default disabled:opacity-40"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelectFilter;
